import { Pagination } from "@mui/material";
import {
  ArchiveIcon,
  AvatarIcon,
  CalendarIcon,
  HomeIcon,
  PersonIcon,
  SewingPinFilledIcon,
} from "@radix-ui/react-icons";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Pbo } from "../../components/Widget/Pbo";
import { Periode } from "../../components/Widget/Periode";
import { SearchInput } from "../../components/Widget/SearchInput";
import { Tabs } from "../../components/Widget/Tab";
import { UploadButton } from "../../components/Widget/UploadButton";
import { store } from "../../services/service";
import { useStoreApp } from "../../store/storeApp";
import { useStoreCommune } from "../../store/storeCommune";
import { useStoreSurvey } from "../../store/SurveyStore";
import {
  ATTRIBUTION_PRESTATAIRE_PAR_LOT,
  EXPORT_EXCEL_RDV_ATTENTE_ATTRIBUTION,
  LISTE_COMMUNE_SANS_PAGINATION,
  PRESTATAIRE,
  RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION,
} from "../../Utils/constant";
import { checkTagValidity } from "../../Utils/Utils";
import { PrestataireAttribution } from "./PrestataireAttribution";
import { PrestataireAttributionEquipe } from "./PrestataireAttributionEquipe";

const HomeAttribution = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [active, setActive] = useState({
    value: "en-attente",
    index: 0,
  });
  const [msg, setMsg] = useState("");
  const [assignModalItem, setAssignModalItem] = useState(null);
  const [selectedPrestataire, setSelectedPrestataire] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedCommune, setSelectedCommune] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const dataListe = useStoreSurvey();
  const prestataire = useStoreApp();
  const communeStore = useStoreCommune();
  const tabs = [
    {
      value: "En attente d'attribution",
      index: 0,
      tag: "LISTE_RDV_EN_ATTENTE_ATTRIBUTION_PRESTATAIRE",
    },
    {
      value: "Attributions effectuées",
      index: 1,
      tag: "LISTE_RDV_ATTRIBUER_PRESTATAIRE",
    },
    {
      value: "Attribution par équipe",
      index: 2,
      tag: "LISTE_RDV_ATTRIBUER_PRESTATAIRE",
    },
  ];

  const filteredTabsData = tabs
    .filter((tab) => checkTagValidity(tab.tag))
    .map((tab, index) => ({ ...tab, index }));

  useEffect(() => {
    if (filteredTabsData.length !== 0) {
      setActive(filteredTabsData[0]);
    }
  }, []);

  // Fonction de recherche avec debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    setIsSearching(true);
    dataListe
      .getData(
        `${RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION}?page=0&param=${value}&size=8&communeId=${selectedCommune}`,
      )
      .finally(() => setIsSearching(false));
  };

  const moreEnAttente = (page) => {
    dataListe.getData(
      `${RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION}?page=${
        page - 1
      }&param=${searchTerm}&size=8&communeId=${selectedCommune}`,
    );
  };

  const handleCommuneChange = (communeId) => {
    setSelectedCommune(communeId);
    dataListe.getData(
      `${RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION}?page=0&param=${searchTerm}&size=8&communeId=${communeId}`,
    );
  };

  const handleAssignPrestataire = async () => {
    if (!assignModalItem || !selectedPrestataire) return;

    setIsAssigning(true);
    setMsg("");

    try {
      const res = await store(
        `/commerciale/rdv/v3/${assignModalItem.abonnement?.id}/attribuer-prestataire?prestataireId=${selectedPrestataire}`,
        "application/json",
        {},
      );

      if (res.status === 200) {
        await dataListe.getData(
          `${RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION}?page=0&param=${searchTerm}&size=8&communeId=${selectedCommune}`,
        );
        document.getElementById("assign-modal").close();
        setAssignModalItem(null);
        setSelectedPrestataire("");
        toast.success("Prestataire affecté avec succès");
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.donnee || "Erreur lors de l'attribution";
      setMsg(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  useEffect(() => {
    prestataire.getAll(PRESTATAIRE);
    communeStore.getData(`${LISTE_COMMUNE_SANS_PAGINATION}`);
  }, []);

  useEffect(() => {
    dataListe.getData(
      `${RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION}?page=0&param=&size=8&communeId=${selectedCommune}`,
    );
  }, [selectedCommune]);

  return (
    <div className="">
      {/* Header */}
      <div className="lg:flex items-center justify-between mb-4">
        <div>
          <h3 className="font-mtn text-3xl font-bold">
            Gestion des attributions
          </h3>
          <p className="text-sm text-gray-500">
            Attribution intelligente des clients aux prestataires
          </p>
        </div>
        <div></div>
      </div>
      <div className="flex justify-between items-center gap-2 mb-4">
        <button
          className="btn btn-sm bg-main border-main flex items-center min-w-[220px] justify-center"
          disabled={exportLoading}
          onClick={async () => {
            setExportLoading(true);
            try {
              const res = await store(
                EXPORT_EXCEL_RDV_ATTENTE_ATTRIBUTION,
                null,
                null,
                { responseType: "blob" },
              );
              if (res?.data) {
                const url = window.URL.createObjectURL(res.data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "rdv_attente_attribution.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
              }
            } catch (err) {
              toast.error("Erreur lors de l'export");
            } finally {
              setExportLoading(false);
            }
          }}
        >
          {exportLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Export en cours...
            </span>
          ) : (
            "Exporter RDV en attente d'attribution"
          )}
        </button>
        <UploadButton
          title="Attribution à prestataire par lot"
          uploadUrl={ATTRIBUTION_PRESTATAIRE_PAR_LOT}
          fetchData={() =>
            dataListe.getData(
              `${RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION}?page=0&param=${searchTerm}&size=8&communeId=${selectedCommune}`,
            )
          }
          model={null}
        />
      </div>
      {/* Tabs */}
      <Tabs active={active} setActive={setActive} tabsData={filteredTabsData} />

      {/* Modal Affecter Prestataire */}
      <dialog id="assign-modal" className="modal">
        <div className="modal-box w-96">
          <h3 className="font-bold text-lg">Affecter un prestataire</h3>
          <p className="text-sm text-gray-500 mb-3">
            Sélectionnez le prestataire à affecter pour ce rendez-vous.
          </p>
          {msg && <p className="text-red-500 text-sm mb-2">{msg}</p>}
          <div className="mb-3">
            <select
              value={selectedPrestataire}
              onChange={(e) => setSelectedPrestataire(e.target.value)}
              className="select select-bordered w-full"
              disabled={isAssigning}
            >
              <option value="">-- Choisir un prestataire --</option>
              {prestataire?.liste?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.designation}
                </option>
              ))}
            </select>
          </div>
          <div className="modal-action">
            <button
              onClick={handleAssignPrestataire}
              disabled={isAssigning || !selectedPrestataire}
              className={`btn btn-sm ${
                isAssigning || !selectedPrestataire
                  ? "btn-disabled opacity-50"
                  : "bg-main text-black border-main"
              }`}
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Affectation...
                </>
              ) : (
                "Affecter"
              )}
            </button>
            <button
              onClick={() => {
                document.getElementById("assign-modal").close();
                setAssignModalItem(null);
                setMsg("");
              }}
              disabled={isAssigning}
              className="btn btn-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      </dialog>

      {/* Contenu principal */}
      {active.index === 0 ? (
        <div className="bg-white rounded-md px-2 py-4 mt-3">
          {/* Filtres */}
          <div className="flex items-center gap-3 mb-4">
            <SearchInput
              onChange={(e) => handleSearch(e.target.value)}
              value={searchTerm}
              isSearching={isSearching}
            />

            <select
              className="select select-bordered h-10 text-xs"
              value={selectedCommune}
              onChange={(e) => handleCommuneChange(e.target.value)}
              disabled={communeStore.loading}
            >
              <option value="">
                {communeStore.loading ? "Chargement..." : "Toutes les communes"}
              </option>
              {communeStore?.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.designation}
                </option>
              ))}
            </select>
          </div>

          {/* Grille des rendez-vous */}
          {dataListe?.loading ? (
            <div className="flex flex-col justify-center items-center py-20 space-y-3">
              <Loader2 className="w-8 h-8 animate-spin text-main" />
              <span className="ml-3 text-gray-600 text-sm">
                Chargement des données...
              </span>
            </div>
          ) : dataListe?.data?.data?.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-sm text-gray-500">Aucune donnée disponible</p>
            </div>
          ) : (
            <>
              <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-2 mt-4 grid-cols-1 gap-5">
                {dataListe?.data?.data?.map((item) => (
                  <div
                    key={item.id}
                    className={`${
                      !item?.inTime
                        ? "border-red-300 border-2 border-dashed"
                        : "border-slate-200 border"
                    } relative bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow`}
                  >
                    <div className="rounded-t-xl flex items-center gap-3 border-b border-dashed border-amber-400 px-2 py-3">
                      <p className="text-[12px] font-semibold">
                        N° {item?.abonnement?.eligibilite?.numeroTicket}
                      </p>
                    </div>
                    <div className="px-3 py-2">
                      <div className="items-center gap-2 space-y-3">
                        <Periode item={item} />
                        <Pbo
                          pbo={item?.abonnement?.eligibilite?.pbo?.nom}
                          port={item?.brin}
                        />
                        <div className="flex gap-1 my-3">
                          <PersonIcon className="bg-gray-100 rounded-full w-8 h-8 p-2" />
                          <div>
                            <p className="text-[13px] font-medium">
                              {item?.abonnement?.client?.nomPrenoms}
                            </p>
                            <p className="text-xs flex font-semibold gap-2">
                              <span>{item?.abonnement.client?.numero}</span>
                              {item?.abonnement.client?.secondNumero && (
                                <>
                                  {" | "}
                                  <span>
                                    {item?.abonnement.client?.secondNumero}
                                  </span>
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className="animate-pulse border-dashed border w-fit px-1 py-[2px] bg-red-50 text-red-800 border-red-600 rounded">
                            <p className="text-xs flex gap-1 items-center font-medium">
                              <CalendarIcon />
                              {item?.dateRdv}
                            </p>
                          </div>
                        </div>

                        <h1 className="text-xs flex gap-1 items-center my-2">
                          Forfait : {item?.abonnement?.forfait?.designation} -{" "}
                          {item?.abonnement?.forfait?.categorie
                            ?.replace("_", "")
                            .toLowerCase()}
                        </h1>

                        <div className="gap-1">
                          <div>
                            <p className="text-[13px] font-medium">
                              Numero FTTH : {item?.abonnement?.numeroFtth}
                            </p>
                            <p className="text-xs flex font-semibold gap-2">
                              SN : <span>{item?.abonnement?.numeroSerie}</span>
                            </p>
                          </div>
                        </div>

                        <h1 className="text-xs flex gap-1 items-center my-2">
                          <AvatarIcon />
                          Equipe en charge : {item?.equipe?.designation}
                        </h1>
                        {item.commentaire && (
                          <div className="dropdown flex items-center">
                            <p className="text-xs">
                              Commentaire: {item?.commentaire}
                            </p>
                          </div>
                        )}
                        <div className="pb-3 pt-1 border-b flex gap-4 items-center">
                          <p className="text-xs flex gap-1 items-center font-medium">
                            <HomeIcon />
                            {item?.abonnement?.batiment?.designation}
                          </p>
                          <p className="text-xs flex gap-1 font-medium">
                            <ArchiveIcon />
                            {item?.abonnement?.equipement?.designation}
                          </p>
                        </div>
                      </div>
                      <h1 className="my-2 text-[13px] font-medium flex items-center">
                        <SewingPinFilledIcon />
                        {item?.abonnement?.commune +
                          ", " +
                          item?.abonnement?.quartier}
                      </h1>
                      <div className="flex items-center gap-1">
                        <h5 className="text-xs font-medium">Etape:</h5>
                        <p className="bg-blue-50 text-blue-900 py-1 px-1 rounded-lg font-medium text-xs truncate">
                          {item?.abonnement?.status
                            ?.toLowerCase()
                            .replace(/_/g, " ")}
                        </p>
                      </div>

                      <div className="flex items-center gap-1">
                        {checkTagValidity("ATTRIBUER_RDV_PRESTATAIRE") && (
                          <button
                            onClick={() => {
                              setAssignModalItem(item);
                              setSelectedPrestataire("");
                              setMsg("");
                              document
                                .getElementById("assign-modal")
                                .showModal();
                            }}
                            className="btn btn-sm bg-main border-main my-3 font-mtn text-xs font-regular hover:bg-opacity-90 transition-colors"
                          >
                            Attribuer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="my-3 flex justify-end">
            <Pagination
              onChange={(event, newValue) => moreEnAttente(newValue)}
              count={dataListe?.data?.totalPages}
              variant="outlined"
              color="primary"
              shape="rounded"
            />
          </div>
        </div>
      ) : active?.index === 1 ? (
        <PrestataireAttribution />
      ) : (
        <PrestataireAttributionEquipe />
      )}
    </div>
  );
};

export default HomeAttribution;
